import {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useRouter } from 'next/router'
import { useCourseData } from '../hooks/use-course-data'
import { useCourseMenu } from '../hooks/use-course-menu'
import { useGetCoursePageUrl } from '../hooks/use-course-path-url'
import { useIsUserAdminOrOwnerOfCourse } from '../hooks/use-is-user-admin-or-owner-of-course'
import type { StepType } from '../hooks/use-step-router/types'
import type { CourseLectureId } from '../types/course-lecture-interface'
import type { CourseQuizId } from '../types/course-quiz-interface'
import { CourseStepEnum } from '../types/course-step-type'
import { mapMenuToSteps } from '../utils/course-steps'
import { getCoursePageQuery } from '../utils/get-course-page-url'
import { parseQueryId } from '../utils/parse-query-id'

export const getContentType = (route: string): string | undefined => {
  const regexp = /\/\[coursePath]\/(.*)\//gm
  const matches = regexp.exec(route)
  return matches?.[1]
}

export const useCourseStepRouter = (): {
  contentRef: RefObject<HTMLDivElement>
  step: StepType | undefined
  steps: StepType[]
  setStepIndex: (value: SetStateAction<number | null>) => void
  onOpenLecture: (id: CourseLectureId) => void
  onOpenQuiz: (id: CourseQuizId) => void
  onPreviousStep: () => void
  onNextStep: () => void
  isInitialized: boolean
  setIsInitialized: (value: boolean) => void
  showUnavailableLecture: boolean
  setShowUnavailableLecture: Dispatch<SetStateAction<boolean>>
  findStep: () => void
  isStart: boolean
  isEnd: boolean
  isPreview: boolean
  areAllModulesUnavailableWithoutSalesPage: boolean | undefined
} => {
  const router = useRouter()
  const coursePath = useGetCoursePageUrl()

  const contentRef = useRef<HTMLDivElement>(null)

  const [steps, setSteps] = useState<StepType[]>([])
  const [prevStepIndex, setPrevStepIndex] = useState<number | null>(null)
  const [stepIndex, setStepIndexState] = useState<number | null>(null)
  const [showUnavailableLecture, setShowUnavailableLecture] = useState<boolean>(true)

  const [isEnd, setIsEnd] = useState<boolean>(true)
  const [isStart, setIsStart] = useState<boolean>(true)

  const { courseData } = useCourseData()
  const courseId = courseData?.id
  const { courseMenu } = useCourseMenu({ courseId })

  const isUserAdminOrOwnerOfCourse = useIsUserAdminOrOwnerOfCourse()

  const step = stepIndex !== null ? (steps[stepIndex] as StepType | undefined) : undefined
  const isInitialized = prevStepIndex !== null

  const setIsInitialized = useCallback((value: boolean) => {
    if (!value) setPrevStepIndex(null)
  }, [])
  const setStepIndex = useCallback(
    (...args: Parameters<typeof setStepIndexState>) => {
      if (prevStepIndex !== stepIndex) {
        setPrevStepIndex(stepIndex)
      }
      setStepIndexState(...args)
    },
    [stepIndex],
  )

  const { lectureId: rawLectureId, quizId: rawQuizId, preview } = router.query
  const lectureId = parseQueryId<CourseLectureId>(rawLectureId)
  const quizId = parseQueryId<CourseQuizId>(rawQuizId)
  const contentType = getContentType(router.route)
  const isPreview = preview === 'true'

  useLayoutEffect(() => {
    if (step && stepIndex !== null) {
      if (steps.slice(0, stepIndex).some(step => step.available)) {
        setIsStart(false)
      } else {
        setIsStart(true)
      }

      if (steps.slice(stepIndex + 1).some(step => step.available)) {
        setIsEnd(false)
      } else {
        setIsEnd(true)
      }
    }
  }, [steps, step, stepIndex])

  useLayoutEffect(() => {
    if (courseMenu) {
      setSteps(mapMenuToSteps(courseMenu))
    }
  }, [courseMenu])

  const findStep = useCallback(() => {
    const currentStepIndex =
      contentType &&
      (lectureId || quizId) &&
      steps.findIndex(
        ({ id: stepId, type }) => type === contentType && [lectureId, quizId].includes(stepId),
      )

    if (stepIndex === currentStepIndex) return

    if (
      typeof currentStepIndex === 'number' &&
      !Number.isNaN(currentStepIndex) &&
      currentStepIndex !== -1 &&
      (steps[currentStepIndex].available || steps[currentStepIndex].module.salesPage)
    ) {
      setStepIndex(currentStepIndex)
    } else if (isPreview || showUnavailableLecture) {
      setStepIndex(null)
    } else {
      const availableSteps = steps.filter(({ available }) => available)
      const uncompletedAvailableSteps = availableSteps.filter(({ completed }) => !completed)

      let lastAvailableUncompletedStepIndex: number | null = null
      if (uncompletedAvailableSteps.length > 0) {
        lastAvailableUncompletedStepIndex = steps.findIndex(
          ({ id }) => id === uncompletedAvailableSteps[0].id,
        )
      } else if (availableSteps.length > 0) {
        lastAvailableUncompletedStepIndex = steps.findIndex(({ id }) => id === availableSteps[0].id)
      } else if (steps.length > 0) {
        lastAvailableUncompletedStepIndex = 0
      }

      if (lastAvailableUncompletedStepIndex === stepIndex) return

      setStepIndex(
        lastAvailableUncompletedStepIndex === -1 ? null : lastAvailableUncompletedStepIndex,
      )

      if (
        coursePath &&
        lastAvailableUncompletedStepIndex &&
        steps[lastAvailableUncompletedStepIndex]
      ) {
        const targetStep = steps[lastAvailableUncompletedStepIndex]
        router.push(
          getCoursePageQuery({
            coursePath,
            type: targetStep.type,
            id: targetStep.id,
          }),
          undefined,
          { shallow: true },
        )
      }
    }
  }, [
    contentType,
    lectureId,
    quizId,
    steps,
    isPreview,
    showUnavailableLecture,
    coursePath,
    router,
    setStepIndex,
    stepIndex,
  ])

  useLayoutEffect(() => {
    if (steps.length > 0 && !isInitialized) {
      findStep()
    }
  }, [findStep, isInitialized, steps.length])

  useEffect(() => {
    if (
      coursePath &&
      router.isReady &&
      step?.id &&
      ([lectureId, quizId].every(id => id !== step.id) || contentType !== step?.type)
    ) {
      router.push(getCoursePageQuery({ coursePath, id: step.id, type: step.type }), undefined, {
        shallow: true,
      })
    }
  }, [courseId, coursePath, step?.id, step?.type, router.isReady, contentType])

  useLayoutEffect(() => {
    // NOTE: show deactivated lecture for a workspace owner or a workspace admin
    if (courseData && (!isUserAdminOrOwnerOfCourse || !contentType || (!lectureId && !quizId))) {
      setShowUnavailableLecture(false)
    }
  }, [courseData, isUserAdminOrOwnerOfCourse, contentType, lectureId, quizId])

  const onOpenLecture = (id: CourseLectureId): void =>
    setStepIndex(steps.findIndex(step => step.type === CourseStepEnum.lecture && step.id === id))

  const onOpenQuiz = (id: CourseQuizId): void =>
    setStepIndex(steps.findIndex(step => step.type === CourseStepEnum.quiz && step.id === id))

  const onPreviousStep = (): void => {
    if (stepIndex !== null && !isStart) {
      setStepIndex(
        steps.findIndex(
          ({ id }) =>
            id ===
            steps
              .slice(0, stepIndex)
              .toReversed()
              .find(step => step.available)?.id,
        ),
      )
    }
  }

  const onNextStep = (): void => {
    if (stepIndex !== null && !isEnd) {
      setStepIndex(
        steps.findIndex(
          ({ id }) => id === steps.slice(stepIndex + 1).find(step => step.available)?.id,
        ),
      )
    }
  }

  const areAllModulesUnavailableWithoutSalesPage = courseMenu?.every(
    module => !module.available && !module.salesPage,
  )

  return {
    contentRef,
    step,
    steps,
    setStepIndex,
    onOpenLecture,
    onOpenQuiz,
    onPreviousStep,
    onNextStep,
    isInitialized,
    setIsInitialized,
    showUnavailableLecture,
    setShowUnavailableLecture,
    findStep,
    isStart,
    isEnd,
    isPreview,
    areAllModulesUnavailableWithoutSalesPage,
  }
}
